// src/components/widgets/EventsWidget.tsx

import { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowUp, ArrowDown } from 'react-feather';
import { useRealtimeService } from '../contexts/RealtimeServiceContext';
import { useFabricService } from '../contexts/FabricServiceContext';
import { MarkdownContainer } from '../components/MarkdownContainer';

interface EventsWidgetProps {
    selectedEventFilters: (string | undefined)[];
}

export const EventsWidget: React.FC<EventsWidgetProps> = ({
    selectedEventFilters,
}) => {

    const [expandedEvents, setExpandedEvents] = useState<{ [key: string]: boolean; }>({});
    const scrollRef = useRef<HTMLDivElement>(null);
    const scrollHeightRef = useRef(0);

    const { realtimeEvents, startTimeRef } = useRealtimeService();
    const { fabricEvents } = useFabricService();

    /**
     * Utility for formatting the timing of logs
     */
    const formatTime = useCallback((timestamp: string) => {
        const startTime = startTimeRef.current;
        const t0 = new Date(startTime).valueOf();
        const t1 = new Date(timestamp).valueOf();
        const delta = t1 - t0;
        const hs = Math.floor(delta / 10) % 100;
        const s = Math.floor(delta / 1000) % 60;
        const m = Math.floor(delta / 60_000) % 60;
        const pad = (n: number) => {
            let s = n + '';
            while (s.length < 2) {
                s = '0' + s;
            }
            return s;
        };
        return `${pad(m)}:${pad(s)}.${pad(hs)}`;
    }, []);

    /**
     * Auto-scroll the event logs
     */
    useEffect(() => {
        if (scrollRef.current) {
            const eventsEl = scrollRef.current;
            const scrollHeight = eventsEl.scrollHeight;
            // Only scroll if height has just changed
            if (scrollHeight !== scrollHeightRef.current) {
                // [TODO] AUTO-SCROLL Diababled, needs to only auto-scroll when needed
                // eventsEl.scrollTop = scrollHeight;
                // scrollHeightRef.current = scrollHeight;
            }
        }
    }, [realtimeEvents, fabricEvents]);

    return (
        <div className="content-block events">
            <div className="content-block-body" ref={scrollRef}>
                {![...realtimeEvents, ...fabricEvents].length && `awaiting connection...`}
                {[...realtimeEvents, ...fabricEvents].sort((a, b) => a.time.localeCompare(b.time)).filter(item => !selectedEventFilters.some(excludeStr => item.event.type.includes(excludeStr))).map((realtimeEvent, i) => {
                    const count = realtimeEvent.count;
                    const event = { ...realtimeEvent.event };
                    if (event.type === 'input_audio_buffer.append') {
                        event.audio = `[trimmed: ${event.audio.length} bytes]`;
                    } else if (event.type === 'response.audio.delta') {
                        event.delta = `[trimmed: ${event.delta.length} bytes]`;
                    }
                    return (
                        <div className="event" key={event.event_id}>
                            <div className="event-timestamp">
                                {formatTime(realtimeEvent.time)}
                            </div>
                            <div className="event-details">
                                <div
                                    className="event-summary"
                                    onClick={() => {
                                        // toggle event details
                                        const id = event.event_id;
                                        const expanded = { ...expandedEvents };
                                        if (expanded[id]) {
                                            delete expanded[id];
                                        } else {
                                            expanded[id] = true;
                                        }
                                        setExpandedEvents(expanded);
                                    }}
                                >
                                    <div
                                        className={`event-source ${event.type === 'error'
                                            ? 'error'
                                            : (`${realtimeEvent.isFabric ? 'fabric-' : ''}${realtimeEvent.source}`)
                                            }`}
                                    >
                                        {realtimeEvent.source === 'client' ? (
                                            <ArrowUp />
                                        ) : (
                                            <ArrowDown />
                                        )}
                                        <span>
                                            {event.type === 'error'
                                                ? 'error!'
                                                : realtimeEvent.source}
                                        </span>
                                    </div>
                                    <div className="event-type">
                                        {event.type}
                                        {count && ` (${count})`}
                                    </div>
                                </div>
                                {!!expandedEvents[event.event_id] && (
                                    <div className="event-payload">
                                        <MarkdownContainer
                                            content={JSON.stringify(event, null, 2)}
                                            type="json"
                                            expandedDefault={true}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};